/**
 * Resolve a label section_number to its registered accordion element.
 *
 * WHY THIS IS A SHARED FUNCTION AND NOT A KEY LOOKUP
 *
 * Sections register their ref under the DATABASE id — ``section-${section.id}``
 * (DrugDetailPage's accordion) — while a citation only carries a
 * ``section_number`` like "6.1". Those two never match, so
 * ``sectionRefs.current.get(`section-${section_number}`)`` returns undefined
 * and every caller that built a key that way silently no-opped: no scroll, no
 * expand, no error. That is exactly what the Sources chips did between
 * 2026-01-25 and 2026-09-09, while the inline section-links in the same answer
 * kept working because they iterated instead.
 *
 * The reliable join is the ``data-section-number`` ATTRIBUTE the accordion
 * stamps on each registered element, which is why this walks the map rather
 * than indexing it. Extracted so the citation path and the section-link path
 * cannot drift apart again.
 */
export interface SectionScrollTarget {
  /** The ref-map key, needed to expand the accordion row. */
  sectionId: string;
  element: HTMLElement;
  /** True when we landed on the parent ("14") of a subsection ("14.7"). */
  viaParent: boolean;
}

export function findSectionScrollTarget(
  entries: Iterable<[string, HTMLElement]>,
  targetSectionNumber: string,
): SectionScrollTarget | null {
  if (!targetSectionNumber) return null;

  // Materialise once: the caller passes a live Map iterator, and the parent
  // fallback below needs a SECOND pass over the same entries.
  const all = Array.from(entries);

  for (const [sectionId, element] of all) {
    if (element.getAttribute('data-section-number') === targetSectionNumber) {
      return { sectionId, element, viaParent: false };
    }
  }

  // Subsection whose own row isn't rendered (e.g. the parent is collapsed, or
  // the label numbers 14 without a 14.7 row): land on the parent rather than
  // doing nothing. Matches the pre-existing section-link behaviour.
  if (targetSectionNumber.includes('.')) {
    const parent = targetSectionNumber.split('.')[0];
    for (const [sectionId, element] of all) {
      if (element.getAttribute('data-section-number') === parent) {
        return { sectionId, element, viaParent: true };
      }
    }
  }

  return null;
}
